// Iter 62cr quater — Client Detail Page (Founder only)
// Full A→Z view for one client : Analytics + Tasks + Notes + Projects + Library.
// Navigated via /clients/:cid.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  ChevronRight, Eye, BarChart3, CheckSquare, FileText, Folder, Video, TrendingUp,
} from '../components/icons';

const STATUS_LABELS = {
  prospect: 'Prospect', onboarding: 'Onboarding', prod: 'En prod', livre: 'Livré',
};

const STAGE_COLORS = {
  todo:        { bg: 'rgba(138,157,184,0.14)', fg: '#B8C4D8', label: 'À faire' },
  in_progress: { bg: 'rgba(91,190,252,0.14)',  fg: '#5BBEFC', label: 'En cours' },
  review:      { bg: 'rgba(167,139,250,0.14)', fg: '#A78BFA', label: 'En revue' },
  done:        { bg: 'rgba(52,211,153,0.14)',  fg: '#34D399', label: 'Terminé' },
};

const TABS = [
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'tasks',     label: 'Tâches',    icon: CheckSquare },
  { id: 'notes',     label: 'Notes',     icon: FileText },
  { id: 'projects',  label: 'Projets',   icon: Video },
  { id: 'library',   label: 'Biblio',    icon: Folder },
];

function fmtNumber(n) {
  n = Number(n || 0);
  if (n >= 1000000) return (n / 1000000).toFixed(1).replace('.0', '') + 'M';
  if (n >= 1000) return (n / 1000).toFixed(1).replace('.0', '') + 'k';
  return String(Math.round(n));
}

function fmtEuro(n) {
  n = Number(n || 0);
  if (n === 0) return '0 €';
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €';
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function ClientDetailPage() {
  const { cid } = useParams();
  const { isFounder } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('analytics');

  useEffect(() => {
    if (!isFounder) { nav('/'); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clients/${cid}/overview`);
      setData(r.data);
    } catch (e) {
      toast.error('Client introuvable');
      nav('/clients');
    } finally {
      setLoading(false);
    }
  };

  if (!isFounder) return null;
  if (loading || !data) {
    return (
      <div className="page-fade" style={{ padding: 22 }}>
        <div className="shimmer" style={{ height: 100, borderRadius: 16, marginBottom: 14 }} />
        <div className="shimmer" style={{ height: 400, borderRadius: 16 }} />
      </div>
    );
  }

  const c = data.client;

  return (
    <div className="page-fade">
      <div style={{ padding: '18px 22px 100px' }}>
        {/* Iter 62cs — Fil d'Ariane : Clients > Nom > Tab */}
        <Breadcrumb
          items={[
            { label: 'Clients', to: '/clients' },
            { label: c.name, to: `/clients/${c.id}` },
            { label: TABS.find((t) => t.id === tab)?.label || 'Analytics' },
          ]}
          accent={c.color}
        />

        <ClientHero client={c} counts={data.counts} analytics={data.analytics} />

        {/* Tabs */}
        <div
          data-testid="client-detail-tabs"
          style={{ display: 'flex', gap: 8, margin: '18px 0 18px', flexWrap: 'wrap' }}
        >
          {TABS.map((t) => {
            const active = tab === t.id;
            const Icon = t.icon;
            const count = data.counts?.[t.id] ?? null;
            return (
              <button
                key={t.id}
                data-testid={`client-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '9px 14px', borderRadius: 999,
                  border: `1px solid ${active ? c.color : 'var(--bd)'}`,
                  background: active
                    ? `linear-gradient(140deg, ${c.color}22, ${c.color}08)`
                    : 'var(--sf)',
                  color: active ? c.color : 'var(--t2)',
                  fontFamily: 'Sora', fontWeight: 800, fontSize: 12.5,
                  cursor: 'pointer',
                  boxShadow: active ? `inset 0 1px 0 rgba(255,255,255,0.08), 0 4px 12px ${c.color}22` : 'none',
                  transition: 'all 0.15s var(--ease-out)',
                }}
              >
                <Icon size={13} />
                {t.label}
                {typeof count === 'number' && count > 0 && (
                  <span style={{
                    padding: '1px 6px', borderRadius: 999,
                    background: active ? c.color : 'var(--sf2)',
                    color: active ? '#0B1220' : 'var(--t3)',
                    fontSize: 10, fontWeight: 800,
                  }}>{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {tab === 'analytics' && <AnalyticsTab client={c} analytics={data.analytics} months={data.analytics.months} />}
        {tab === 'tasks'     && <TasksTab tasks={data.tasks} client={c} />}
        {tab === 'notes'     && <NotesTab notes={data.notes} client={c} />}
        {tab === 'projects'  && <ProjectsTab projects={data.projects} client={c} />}
        {tab === 'library'   && <LibraryTab files={data.files} client={c} />}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Iter 62cs — Fil d'Ariane (breadcrumb) — items = [{label, to?}]
function Breadcrumb({ items, accent = '#5BBEFC' }) {
  const nav = useNavigate();
  return (
    <nav
      data-testid="client-detail-breadcrumb"
      aria-label="Fil d'Ariane"
      style={{
        display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap',
        marginBottom: 14, marginTop: 4,
      }}
    >
      {items.map((it, i) => {
        const isLast = i === items.length - 1;
        const clickable = !!it.to && !isLast;
        return (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {clickable ? (
              <button
                data-testid={`breadcrumb-${i}`}
                onClick={() => nav(it.to)}
                style={{
                  background: 'none', border: 'none', padding: '4px 6px',
                  borderRadius: 6,
                  color: 'var(--t3)',
                  fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
                  cursor: 'pointer', letterSpacing: '-0.005em',
                  transition: 'all 0.12s var(--ease-out)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = accent; e.currentTarget.style.background = 'var(--sf)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--t3)'; e.currentTarget.style.background = 'none'; }}
              >{it.label}</button>
            ) : (
              <span
                data-testid={`breadcrumb-${i}`}
                style={{
                  padding: '4px 6px',
                  color: isLast ? accent : 'var(--t3)',
                  fontFamily: 'Sora', fontWeight: isLast ? 800 : 700,
                  fontSize: 12.5, letterSpacing: '-0.005em',
                }}
              >{it.label}</span>
            )}
            {!isLast && (
              <ChevronRight size={11} color="var(--t4)" strokeWidth={2} />
            )}
          </span>
        );
      })}
    </nav>
  );
}

// -----------------------------------------------------------------------------
function ClientHero({ client, counts, analytics }) {
  const color = client.color || '#5BBEFC';
  const initials = (client.name || '?').split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('');
  const positive = (analytics.views_growth_pct || 0) >= 0;
  return (
    <div
      data-testid="client-detail-hero"
      style={{
        display: 'flex', alignItems: 'center', gap: 18,
        padding: '20px 22px',
        borderRadius: 20,
        background: `linear-gradient(140deg, ${color}18 0%, rgba(255,255,255,0.02) 55%, ${color}08 100%)`,
        border: `1px solid ${color}44`,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.08), 0 8px 26px ${color}22`,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 68, height: 68, borderRadius: 18,
          display: 'grid', placeItems: 'center',
          background: `linear-gradient(140deg, ${color} 0%, rgba(255,255,255,0.15) 50%, ${color} 100%)`,
          color: '#0B1220', fontFamily: 'Sora', fontWeight: 800, fontSize: 22,
          boxShadow: `inset 0 1px 0 rgba(255,255,255,0.55), inset 0 -8px 12px rgba(0,0,0,0.25), 0 8px 20px ${color}55`,
          flexShrink: 0,
        }}
      >{initials || '?'}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: 'Sora', fontWeight: 800, fontSize: 26,
          color: 'var(--t1)', letterSpacing: '-0.02em',
        }}>{client.name}</div>
        <div style={{
          display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap',
          fontFamily: 'Sora', fontWeight: 700, fontSize: 11,
          color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.14em',
        }}>
          <span>{STATUS_LABELS[client.status] || client.status}</span>
          <span>· {counts.projects} projet{counts.projects > 1 ? 's' : ''}</span>
          <span>· {counts.tasks} tâche{counts.tasks > 1 ? 's' : ''}</span>
          <span>· {counts.notes} note{counts.notes > 1 ? 's' : ''}</span>
          <span>· {counts.files} fichier{counts.files > 1 ? 's' : ''}</span>
        </div>
      </div>
      {/* KPI mini */}
      <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
        <KpiPill label="Revenu 6m" value={fmtEuro(analytics.total_revenue)} accent="#F5C56A" icon={<BarChart3 size={11} />} />
        <KpiPill label="Vues" value={fmtNumber(analytics.total_views)} accent="#5BBEFC" icon={<Eye size={11} />} />
        <KpiPill
          label="Croissance"
          value={`${positive ? '+' : ''}${Math.round(analytics.views_growth_pct || 0)}%`}
          accent={positive ? '#34D399' : '#FF6B6B'}
          icon={<TrendingUp size={11} style={{ transform: positive ? 'none' : 'scaleY(-1)' }} />}
        />
      </div>
    </div>
  );
}

function KpiPill({ label, value, accent, icon }) {
  return (
    <div style={{
      padding: '10px 14px', borderRadius: 12,
      border: '1px solid var(--bd)', background: 'var(--sf)',
      minWidth: 108,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 5,
        fontFamily: 'Sora', fontWeight: 700, fontSize: 9.5,
        color: accent, textTransform: 'uppercase', letterSpacing: '0.14em',
      }}>{icon}{label}</div>
      <div style={{
        fontFamily: 'Sora', fontWeight: 800, fontSize: 17,
        color: 'var(--t1)', marginTop: 3,
      }}>{value}</div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function AnalyticsTab({ client, analytics, months }) {
  const monthly = analytics.monthly || [];
  const maxViews = Math.max(1, ...monthly.map((m) => m.views || 0));
  const maxRev = Math.max(1, ...monthly.map((m) => m.revenue || 0));
  return (
    <div data-testid="client-analytics-tab" style={{
      borderRadius: 16, border: '1px solid var(--bd)',
      background: 'var(--sf)', padding: 22,
    }}>
      <div style={{
        fontFamily: 'Sora', fontWeight: 800, fontSize: 12,
        color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.16em',
        marginBottom: 14,
      }}>Évolution 6 mois</div>
      {/* Sparkline */}
      <svg width="100%" viewBox="0 0 640 200" preserveAspectRatio="none" style={{ display: 'block', height: 200, marginBottom: 6 }}>
        <line x1="0" x2="640" y1="180" y2="180" stroke="var(--bd)" strokeWidth="1" />
        {monthly.map((m, i) => {
          const step = 640 / monthly.length;
          const barW = step * 0.55;
          const x = step * i + (step - barW) / 2;
          const h = ((m.views || 0) / maxViews) * 150;
          const y = 180 - h;
          return (
            <rect key={`b${i}`} x={x} y={y} width={barW} height={Math.max(2, h)} rx="3" fill="#5BBEFC" opacity={m.views > 0 ? 0.85 : 0.22} />
          );
        })}
        {maxRev > 0 && (() => {
          const step = 640 / monthly.length;
          const pts = monthly.map((m, i) => ({ x: step * i + step / 2, y: 180 - ((m.revenue || 0) / maxRev) * 150 }));
          const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
          return (
            <>
              <path d={path} fill="none" stroke="#F5C56A" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              {pts.map((p, i) => (<circle key={`c${i}`} cx={p.x} cy={p.y} r="3.5" fill="#F5C56A" />))}
            </>
          );
        })()}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-around', marginBottom: 20 }}>
        {monthly.map((m) => (
          <div key={m.month} style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: 10,
            color: 'var(--t4)', textTransform: 'uppercase', letterSpacing: '0.1em',
          }}>{m.month.slice(5, 7)}/{m.month.slice(2, 4)}</div>
        ))}
      </div>
      {/* Legend */}
      <div style={{ display: 'flex', gap: 22, alignItems: 'center', fontFamily: 'Sora', fontSize: 12, color: 'var(--t3)' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 14, height: 14, borderRadius: 3, background: '#5BBEFC' }} /> Vues cumulées
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 14, height: 2, background: '#F5C56A', borderRadius: 2 }} /> Revenu mensuel
        </span>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
function TasksTab({ tasks, client }) {
  const nav = useNavigate();
  if (tasks.length === 0) return <EmptyState label="Aucune tâche liée à ce client" />;
  return (
    <div data-testid="client-tasks-tab" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {tasks.map((t) => {
        const stage = STAGE_COLORS[t.stage] || STAGE_COLORS.todo;
        return (
          <button
            key={t.id}
            data-testid={`client-task-${t.id}`}
            onClick={() => nav(`/tasks?open=${t.id}`)}
            style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 14px', borderRadius: 12,
              border: '1px solid var(--bd)', background: 'var(--sf)',
              cursor: 'pointer', textAlign: 'left',
              transition: 'all 0.15s var(--ease-out)',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = client.color; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; }}
          >
            <CheckSquare size={14} color={stage.fg} strokeWidth={1.8} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: 'Sora', fontWeight: 700, fontSize: 13.5,
                color: 'var(--t1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>{t.title}</div>
              <div style={{
                display: 'flex', gap: 8, marginTop: 3, alignItems: 'center',
                fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5,
                color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: '0.1em',
              }}>
                <span style={{
                  padding: '2px 7px', borderRadius: 999,
                  background: stage.bg, color: stage.fg, fontWeight: 800,
                }}>{stage.label}</span>
                {t.due_date && <span>· {fmtDate(t.due_date)}</span>}
                {t.kind === 'video' && <span style={{ color: '#A78BFA' }}>· Vidéo</span>}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------------------
function NotesTab({ notes, client }) {
  const nav = useNavigate();
  if (notes.length === 0) return <EmptyState label="Aucune note liée à ce client" />;
  return (
    <div data-testid="client-notes-tab" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {notes.map((n) => (
        <button
          key={n.id}
          data-testid={`client-note-${n.id}`}
          onClick={() => nav(`/notes?note=${n.id}`)}
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 12,
            padding: '12px 14px', borderRadius: 12,
            border: '1px solid var(--bd)', background: 'var(--sf)',
            cursor: 'pointer', textAlign: 'left',
            transition: 'all 0.15s var(--ease-out)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = client.color; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; }}
        >
          <FileText size={14} color={client.color} strokeWidth={1.8} style={{ marginTop: 1, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: 'Sora', fontWeight: 700, fontSize: 13.5,
              color: 'var(--t1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{n.title || 'Sans titre'}</div>
            <div style={{
              fontFamily: 'Sora', fontWeight: 600, fontSize: 11,
              color: 'var(--t3)', marginTop: 3,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>
              {(n.content || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').slice(0, 140) || '—'}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
function ProjectsTab({ projects, client }) {
  const nav = useNavigate();
  if (projects.length === 0) return <EmptyState label="Aucun projet pour ce client" />;
  return (
    <div data-testid="client-projects-tab" style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      gap: 10,
    }}>
      {projects.map((p) => (
        <button
          key={p.id}
          data-testid={`client-project-${p.id}`}
          onClick={() => { localStorage.setItem('asa_open_project', p.id); nav('/projects'); }}
          style={{
            display: 'flex', flexDirection: 'column', gap: 8,
            padding: '14px 16px', borderRadius: 14,
            border: '1px solid var(--bd)', background: 'var(--sf)',
            cursor: 'pointer', textAlign: 'left',
            transition: 'all 0.15s var(--ease-out)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = p.color || client.color; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              width: 30, height: 30, borderRadius: 8,
              display: 'grid', placeItems: 'center',
              background: `${p.color || client.color}22`,
              border: `1px solid ${p.color || client.color}55`,
              color: p.color || client.color,
            }}>
              <Video size={13} strokeWidth={2} />
            </span>
            <div style={{
              fontFamily: 'Sora', fontWeight: 800, fontSize: 14,
              color: 'var(--t1)', letterSpacing: '-0.01em',
              flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{p.name}</div>
          </div>
          {p.description && (
            <div style={{
              fontFamily: 'Sora', fontWeight: 500, fontSize: 11.5,
              color: 'var(--t3)',
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>{p.description}</div>
          )}
        </button>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
function LibraryTab({ files, client }) {
  const nav = useNavigate();
  if (files.length === 0) return <EmptyState label="Aucun fichier partagé avec ce client" cta="Ouvrir la bibliothèque" onCta={() => nav('/library')} />;
  return (
    <div data-testid="client-library-tab" style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
      gap: 10,
    }}>
      {files.map((f) => (
        <a
          key={f.id}
          data-testid={`client-file-${f.id}`}
          href={f.kind === 'link' ? f.url : `/library`}
          target={f.kind === 'link' ? '_blank' : undefined}
          rel="noreferrer"
          onClick={(e) => {
            if (f.kind !== 'link') { e.preventDefault(); nav('/library'); }
          }}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 14px', borderRadius: 12,
            border: '1px solid var(--bd)', background: 'var(--sf)',
            cursor: 'pointer', textDecoration: 'none',
            transition: 'all 0.15s var(--ease-out)',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = client.color; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--bd)'; }}
        >
          <Folder size={14} color={client.color} strokeWidth={1.8} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: 'Sora', fontWeight: 700, fontSize: 13,
              color: 'var(--t1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{f.original_name || 'Fichier'}</div>
            <div style={{
              fontFamily: 'Sora', fontWeight: 600, fontSize: 10.5,
              color: 'var(--t3)', marginTop: 2,
              textTransform: 'uppercase', letterSpacing: '0.1em',
            }}>{f.kind === 'link' ? 'Lien externe' : 'Fichier'}</div>
          </div>
        </a>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
function EmptyState({ label, cta, onCta }) {
  return (
    <div style={{
      padding: '50px 20px', textAlign: 'center',
      color: 'var(--t3)', fontFamily: 'Sora', fontSize: 13,
      border: '1px dashed var(--bd)', borderRadius: 16,
    }}>
      <FileText size={26} color="var(--t4)" strokeWidth={1.4} />
      <div style={{ marginTop: 10, fontWeight: 700, color: 'var(--t2)' }}>{label}</div>
      {cta && (
        <button
          onClick={onCta}
          style={{
            marginTop: 12, padding: '8px 14px', borderRadius: 999,
            border: '1px solid var(--bd)', background: 'var(--sf)',
            color: 'var(--t2)', fontFamily: 'Sora', fontWeight: 700, fontSize: 12,
            cursor: 'pointer',
          }}
        >{cta}</button>
      )}
    </div>
  );
}
